import { OrderedList, FormComponent, ShopStyle } from "./TicketApi";

export interface PreRegistrationEvent {
    eventId: string;
    name: string;
    headerImage?: string;
    location: {
        name: string;
    };
    startDate: string;
    endDate: string;
}

export interface Language {
    code: string;
    name: string;
    flagIcon: string;
}

export interface EventThemeImage {
    id: string;
    url: string;
}

export interface EventThemeApi {
    background: string;
    foreground: string;
    card: string;
    cardForeground: string;
    cardBorder: string;
    primary: string;
    primaryForeground: string;
    primaryBorder: string;
    secondary: string;
    secondaryForeground: string;
    secondaryBorder: string;
    accent: string;
    accentForeground: string;
    muted: string;
    mutedForeground: string;
    destructive: string;
    destructiveForeground: string;
    border: string;
    input: string;
    ring: string;
    navBarBg: string;
    navBarBorder: string;
    navBarTextColor: string;
    navBarTextColorActive: string;
    navBarTextNotificationColor: string;
    navBarActiveBackgroundColor: string;
    backgroundImage: EventThemeImage | null;
    headerImage: EventThemeImage | null;
    isStatusBarDark: boolean;
}

export interface EventTheme {
    light: EventThemeApi;
    dark: EventThemeApi;
}

export interface PreRegistrationShop {
    slug: string;
    defaultLanguage: string;
    supportedLanguages: Language[];
    /** Legacy shop styling, still returned for backward compatibility. */
    style: ShopStyle;
    /** The event's theme; prefer this over `style` when present. */
    theme?: EventTheme;
    /** ISO-8601 with offset, e.g. "2026-09-01T12:00:00+00:00". */
    availableUntil: string|null;
    event: PreRegistrationEvent;
    form: FormComponent[];
}

export interface PreRegistration {
    registrationId?: string;
    form: OrderedList<FormComponent>;
    answers: Record<string, string | string[] | object[]>;
    errors: Record<string, string[]>;
    /** Organizer-configured thank-you message, plain text. */
    message?: string;
    /**
     * The same message as rich text (HTML), already sanitized by the API through a
     * tag allowlist. Absent on an API that predates rich text — fall back to `message`.
     */
    messageHtml?: string;
}

interface ApiResponse<T> {
    data: T;
}

interface ApiErrorResponse {
    errors: Record<string, string[]>;
}

export class PreRegistrationApi {
    constructor(
        private readonly baseUrl: string,
        private readonly shopSlug: string,
    ) {}

    public async loadShop(language: string): Promise<PreRegistrationShop> {
        const response = await fetch(`${this.baseUrl}/pre-registration/${this.shopSlug}?language=${language}`);
        if (!response.ok) {
            throw new Error('Failed to load pre-registration shop');
        }
        const json = await response.json() as ApiResponse<PreRegistrationShop>;
        return json.data;
    }

    public async createRegistration(language: string): Promise<PreRegistration> {
        // Convert array form to OrderedList format
        const shop = await this.loadShop(language);
        const formAsOrderedList: Record<number, FormComponent> = {};
        shop.form.forEach((component, index) => {
            formAsOrderedList[index] = component;
        });

        return {
            form: formAsOrderedList,
            answers: {},
            errors: {}
        };
    }

    public async submitRegistration(answers: Record<string, string | string[] | object[]>, language: string): Promise<PreRegistration> {
        const response = await fetch(`${this.baseUrl}/pre-registration/${this.shopSlug}/register`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ answers, language }),
        });

        const data = await response.json();

        if (!response.ok) {
            const errorResponse = data as ApiErrorResponse;
            // Return a PreRegistration object with the errors
            const shop = await this.loadShop(language); // Re-fetch the form structure
            const formAsOrderedList: Record<number, FormComponent> = {};
            shop.form.forEach((component, index) => {
                formAsOrderedList[index] = component;
            });

            return {
                form: formAsOrderedList,
                answers,
                errors: errorResponse.errors
            };
        }

        // If successful, return a clean PreRegistration object with the server's thank-you message
        const successResponse = data as { message?: string; messageHtml?: string };
        return {
            form: {}, // Form not needed after successful submission
            answers: {},
            errors: {},
            message: typeof successResponse.message === 'string' ? successResponse.message : undefined,
            messageHtml: typeof successResponse.messageHtml === 'string' ? successResponse.messageHtml : undefined
        };
    }
} 